import { Image } from '@skytech/ui/Image'
// @ts-ignore
import { humanize } from 'i/lib/methods'
import CellphoneIcon from 'mdi-react/CellphoneIcon'
import CheckIcon from 'mdi-react/CheckIcon'
import HammerWrenchIcon from 'mdi-react/HammerWrenchIcon'
import LaptopIcon from 'mdi-react/LaptopIcon'
import PackageVariantIcon from 'mdi-react/PackageVariantIcon'
import SortNumericAscendingIcon from 'mdi-react/SortNumericAscendingIcon'
import SortNumericDescendingIcon from 'mdi-react/SortNumericDescendingIcon'
import TabletIcon from 'mdi-react/TabletIcon'
import ReactHtmlParser from 'react-html-parser'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import { FilterInput } from '@/components/form/Input'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useText } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Hits from '@/components/primitives/Hits'
import Highlight from '@/components/util/Highlight'
import { activatePopup } from '@/ducks/popupReducer'
import {
  addProduct,
  createProductSelection,
  getIsCompleted,
  getIsLeasing,
  getIsPaymentTypeSet,
  getIsProductSelected,
} from '@/ducks/shopOrderReducer'
import {
  getIsLeasingAvailable,
  getMainProductTypeFilter,
  getShowOnlyInStock,
  getSortedFilteredMainProducts,
  getSortOrder,
  getSortParam,
  getTextFilter,
  setShowOnlyInStock,
  setSortOrder,
  setSortParam,
  setTextFilter,
  toggleMainProductTypeFilter,
} from '@/ducks/shopProductsReducer'
import withShopProductsLoaded from '@/hocs/withShopProductsLoaded'
import useRenderLimit from '@/hooks/render-limit-hook'
import { useIsBechtle } from '@/hooks/user'
import { getRegexSafeString, SORT_ORDER } from '@/util/misc'
import { PRODUCT_TYPES } from '@/util/shop'

import ProductAddDialog from './ProductAddDialog'
import ProductRemovalDialog from './ProductRemovalDialog'

import './step-main-product.scss'

const StepMainProduct = () => {
  const products = useSelector(getSortedFilteredMainProducts)

  const { visibleList: visibleProducts, ListLoaderComponent } = useRenderLimit(products, 21, 9)

  const paddingLength = 3 - (visibleProducts.length % 3)

  return (
    <div className="shop-step__main-product">
      <Filter />

      <Hits amount={products.length} />

      <div className="shop-step__main-product__products">
        {visibleProducts.map(product => (
          <Product key={product.id} {...product} />
        ))}

        {paddingLength >= 1 && <div className="product padding" />}

        {paddingLength === 2 && <div className="product padding" />}
      </div>
      <ListLoaderComponent />
    </div>
  )
}

const Filter = () => {
  const dispatch = useDispatch()
  const textFilter = useSelector(getTextFilter)
  const typeFilter = useSelector(getMainProductTypeFilter)
  const isBechtle = useIsBechtle()
  const sortParam = useSelector(getSortParam)
  const sortOrder = useSelector(getSortOrder)
  const showOnlyInStock = useSelector(getShowOnlyInStock)

  const productFilterPlaceholderText = useText('supersearch...')
  const sortByPriceTitle = useText(`sort by price`)
  const showOnlyInStockTitle = useText(`show only products in stock`)

  const types = [PRODUCT_TYPES.PC, PRODUCT_TYPES.MOBILE_PHONE, PRODUCT_TYPES.TABLET]

  const toggleSorting = () => {
    if (!sortParam) {
      dispatch(setSortParam('buying_price'))
      return
    }
    if (sortOrder === SORT_ORDER.ASC) {
      dispatch(setSortOrder(SORT_ORDER.DESC))
      return
    }
    dispatch(setSortOrder(SORT_ORDER.ASC))
    dispatch(setSortParam(undefined))
  }

  return (
    <div className="shop-step__main-product__filter">
      <FilterInput
        type="text"
        placeholder={productFilterPlaceholderText}
        value={textFilter}
        onChange={e => dispatch(setTextFilter(e.target.value))}
      />

      <div className="input-container sorting" title={sortByPriceTitle}>
        <button className={sortParam === 'buying_price' ? 'selected' : ''} onClick={toggleSorting}>
          {sortOrder === SORT_ORDER.ASC ? (
            <SortNumericAscendingIcon />
          ) : (
            <SortNumericDescendingIcon />
          )}
        </button>
      </div>

      {!isBechtle && (
        <div className="input-container stock" title={showOnlyInStockTitle}>
          <button
            className={showOnlyInStock ? 'selected' : ''}
            onClick={() => dispatch(setShowOnlyInStock(!showOnlyInStock))}
          >
            <PackageVariantIcon />
          </button>
        </div>
      )}

      {!isBechtle &&
        types.map(type => <TypeFilter key={type} type={type} isSelected={typeFilter === type} />)}
    </div>
  )
}

const TypeFilter = ({ type, isSelected = false }: any) => {
  const dispatch = useDispatch()
  const title = useText(`show only {type}`, { type: humanize(type).toLowerCase() })

  return (
    <div className="input-container" key={type} title={title}>
      <button
        onClick={e => {
          dispatch(toggleMainProductTypeFilter(type))
          // @ts-ignore
          e.target.blur()
        }}
        className={isSelected ? 'selected' : ''}
      >
        <TypeIcon type={type} />
      </button>
    </div>
  )
}

export const Product = ({
  id,
  product_no,
  name,
  buying_price,
  leasing_price,
  leasing_duration_months,
  image_url,
  type,
  in_stock,
  desc,
}: any) => {
  const dispatch = useDispatch()
  const { whiteLabel } = useAppContext()
  const textFilter = getRegexSafeString(useSelector(getTextFilter))
  const isSelected = useSelector(state => getIsProductSelected(state, id))
  const isLeasingAvailable = useSelector(getIsLeasingAvailable)
  const isLeasing = useSelector(getIsLeasing)
  const isPaymentTypeSet = useSelector(getIsPaymentTypeSet)
  const isCompleted = useSelector(getIsCompleted)
  const isBechtle = useIsBechtle()

  const showLeasingPrice = isLeasing || (isLeasingAvailable && !isPaymentTypeSet)

  const product = createProductSelection({
    productId: id,
    amount: 1,
  })

  const openDialog = () => dispatch(activatePopup(<ProductRemovalDialog productId={id} />))

  const onClickAdd = () =>
    isPaymentTypeSet
      ? dispatch(addProduct(product))
      : dispatch(activatePopup(<ProductAddDialog productId={id} />))

  const onClick = () => (isSelected ? openDialog() : onClickAdd())

  const nameParts = name.split(' ')
  const namePartsFirst3 = nameParts.slice(0, 2).join(' ')
  const namePartsFinal = nameParts.slice(2, nameParts.length).join(' ')

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`product ${isSelected ? 'selected' : ''} wl-${whiteLabel}`}
      // @ts-ignore
      onClick={!isCompleted && onClick}
    >
      <div className="img">
        <div className="vertical-helper" />
        {image_url ? <Image src={image_url} alt={name} /> : <TypeIcon type={type} />}
      </div>

      {showLeasingPrice ? (
        <div className="price">
          <FormattedNumberWithCurrency amount={leasing_price / leasing_duration_months} />

          <Translation
            id="/ {leasing_duration_months}m"
            leasing_duration_months={leasing_duration_months}
          />
        </div>
      ) : (
        <div className="price">
          <FormattedNumberWithCurrency amount={buying_price} />
        </div>
      )}

      {!isBechtle && (
        <div className="in-stock">
          <Translation id="Stock: {n}" n={in_stock} />
        </div>
      )}

      <div className="check-mark">
        <CheckIcon />
      </div>

      {!isBechtle && (
        <>
          <div className="model">
            <Highlight pattern={textFilter} text={namePartsFirst3} />
          </div>

          <div className="name">
            <Highlight pattern={textFilter} text={namePartsFinal} />
          </div>
        </>
      )}

      {isBechtle && (
        <>
          <div className="model">
            <Highlight pattern={textFilter} text={namePartsFirst3} />
          </div>

          <div className="name">
            <Highlight pattern={textFilter} text={namePartsFinal} />
          </div>

          <div className="product-no">
            <Highlight pattern={textFilter} text={product_no} />
          </div>
        </>
      )}

      {desc && <div className="desc">{ReactHtmlParser(desc)}</div>}
    </div>
  )
}

const TypeIcon = ({ type }: any) => {
  switch (type) {
    case PRODUCT_TYPES.PC:
      return <LaptopIcon />
    case PRODUCT_TYPES.TABLET:
      return <TabletIcon />
    case PRODUCT_TYPES.SERVICE:
      return <HammerWrenchIcon />
    case PRODUCT_TYPES.MOBILE_PHONE:
    default:
      return <CellphoneIcon />
  }
}

export default withShopProductsLoaded(StepMainProduct)
